import type { QueryClient } from '@tanstack/react-query'
import {
  findSberAutoLeadByExternalId,
  upsertSberAutoLead,
  type SberAutoLeadPayload,
} from '@/api/inspection-leads'
import { findPartnerLeadByExternalId, partnerStatusLabelKey, upsertPartnerLead } from '@/api/partner'
import { leadStatusLabelKey, type InspectionLeadStatus } from '@/types/inspection-lead'
import i18n from '@/utils/i18n'

/**
 * Куда форма сохраняет заявку и как ищет дубль по номеру CRM. Сотрудники —
 * RPC upsert_sberauto_lead и прямое чтение лидов; партнёр в кабинете —
 * partner_lead_upsert / partner_leads (таблицы ему закрыты).
 */
export interface SberLeadIntake {
  id: string
  save: (payload: SberAutoLeadPayload) => Promise<{ leadId: string; created: boolean }>
  findByExternalId: (
    externalId: string,
  ) => Promise<{ client_name: string | null; status: InspectionLeadStatus } | null>
  leadHref: (leadId: string) => string
  invalidate: (qc: QueryClient, leadId: string) => void
  /** Ключ подписи статуса (у партнёра — без отделов ФО/ОО). */
  statusLabelKey: (status: InspectionLeadStatus) => string
  /** Свои подписи (`<prefix>.<имя>`), остальные — `leads.sber.<имя>`. */
  textPrefix?: string
}

export const STAFF_SBER_INTAKE: SberLeadIntake = {
  id: 'staff',
  save: upsertSberAutoLead,
  findByExternalId: findSberAutoLeadByExternalId,
  leadHref: (id) => `/inspection-leads/${id}`,
  statusLabelKey: (s) => leadStatusLabelKey('status', s, 'sberauto', (k) => i18n.exists(k)),
  invalidate: (qc, leadId) => {
    void qc.invalidateQueries({ queryKey: ['inspection-leads'] })
    void qc.invalidateQueries({ queryKey: ['lead-stage-counts'] })
    void qc.invalidateQueries({ queryKey: ['inspection-lead', leadId] })
  },
}

export const PARTNER_SBER_INTAKE: SberLeadIntake = {
  id: 'partner',
  save: upsertPartnerLead,
  findByExternalId: findPartnerLeadByExternalId,
  leadHref: (id) => `/partner/leads/${id}`,
  statusLabelKey: (s) => partnerStatusLabelKey(s, 'sberauto'),
  invalidate: (qc, leadId) => {
    void qc.invalidateQueries({ queryKey: ['partner-leads'] })
    void qc.invalidateQueries({ queryKey: ['partner-lead', leadId] })
  },
  textPrefix: 'partner.form',
}
